import classNames from 'classnames'
import { CSSProperties, FC } from 'react'
import styles from './skeleton.module.scss'

interface SkeletonProps {
  // Render as a circle. Height falls back to width, so a single `width` is
  // enough for the common avatar/rank-badge case.
  circle?: boolean
  className?: string
  height?: number | string
  // Corner radius override. Ignored when `circle` is set.
  radius?: number | string
  width?: number | string
}

const toCssSize = (value?: number | string): string | undefined =>
  typeof value === 'number' ? `${value}px` : value

/**
 * A single shimmering placeholder block.
 *
 * Compose these to mirror the SHAPE of the content that is loading — matching
 * row counts, sizes and wrappers — so the widget occupies its final footprint
 * before the data lands and nothing shifts when it does. A skeleton that does
 * not match the real layout still causes the jump it is meant to prevent.
 *
 * The shimmer itself is the easy part; the structural match is the point.
 */
export const Skeleton: FC<SkeletonProps> = ({
  circle = false,
  className,
  height,
  radius,
  width,
}) => {
  const style: CSSProperties = {
    height: toCssSize(height ?? (circle ? width : undefined)),
    width: toCssSize(width),
  }

  if (circle) {
    style.borderRadius = '50%'
  } else if (radius !== undefined) {
    style.borderRadius = toCssSize(radius)
  }

  return (
    <span
      aria-hidden
      className={classNames(styles.skeleton, className)}
      style={style}
    />
  )
}
